import React, { useEffect, useState, cloneElement } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { HeartIcon, BookIcon, HeartHandshakeIcon, UsersIcon, ListIcon, AlertCircleIcon, BookOpenIcon, MapPinIcon, UserIcon, FlameIcon, LinkIcon, ZapIcon, BookmarkIcon, GlobeIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, NavItem, FilterSection } from '../components/UI/FilterSidebar';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { ContentAccordionSection, sortEntriesAlphabetically } from '../components/UI/ContentAccordion';
import { TagList, TagViewToggle, type TagDisplayMode } from '../components/Content/TagDisplay';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useDarkMode } from '../context/DarkModeContext';
// Type definitions for our data
type CategoryKey = 'all' | 'romance' | 'fantasy' | 'mystery' | 'scifi' | 'horror' | 'general';
interface Category {
  key: CategoryKey | 'all';
  label: string;
  icon: JSX.Element;
  color: string;
}
interface Trope {
  id: string;
  name: string;
  category: CategoryKey;
  count: number;
  popular: boolean;
  description?: string;
}
const Tropes: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dk = useDarkClasses();
  const {
    darkMode
  } = useDarkMode();
  // State for filters and search
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<'popularity' | 'alphabetical' | 'bookCount' | 'viewAll'>('alphabetical');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tagViewMode, setTagViewMode] = useState<TagDisplayMode>('cards');
  // Sync selectedCategory from URL params reactively
  useEffect(() => {
    const category = searchParams.get('category');
    setSelectedCategory(category as CategoryKey || 'all');
  }, [searchParams]);
  // Categories with their associated colors and icons
  const categories: Category[] = [{
    key: 'all',
    label: 'All Tropes',
    icon: <HeartIcon className="h-4 w-4" data-id="element-15702" data-mp="34g2wwyv6vq6k" />,
    color: 'bg-indigo-100 text-indigo-800'
  }, {
    key: 'romance',
    label: 'Romance',
    icon: <HeartIcon className="h-4 w-4" data-id="element-15703" data-mp="26tng7zal6wk2" />,
    color: 'bg-pink-100 text-pink-800'
  }, {
    key: 'fantasy',
    label: 'Fantasy',
    icon: <BookIcon className="h-4 w-4" data-id="element-15704" data-mp="2gph1q86aa0dz" />,
    color: 'bg-indigo-100 text-indigo-800'
  }, {
    key: 'mystery',
    label: 'Mystery & Thriller',
    icon: <BookIcon className="h-4 w-4" data-id="element-15705" data-mp="2kphdfl1zzxhc" />,
    color: 'bg-amber-100 text-amber-800'
  }, {
    key: 'scifi',
    label: 'Sci-Fi',
    icon: <BookIcon className="h-4 w-4" data-id="element-15706" data-mp="tlqavl4omq" />,
    color: 'bg-cyan-100 text-cyan-800'
  }, {
    key: 'horror',
    label: 'Horror',
    icon: <BookIcon className="h-4 w-4" data-id="element-15707" data-mp="1kim0uko6ew5y" />,
    color: 'bg-red-100 text-red-800'
  }, {
    key: 'general',
    label: 'General',
    icon: <BookIcon className="h-4 w-4" data-id="element-15708" data-mp="1bqttiiug835l" />,
    color: 'bg-gray-100 text-gray-800'
  }];
  // Sample tropes data
  const tropes: Trope[] = [
  // Romance Tropes
  {
    id: '1',
    name: 'Enemies to Lovers',
    category: 'romance',
    count: 356,
    popular: true,
    description: 'Characters who initially dislike or hate each other eventually fall in love.'
  }, {
    id: '2',
    name: 'Friends to Lovers',
    category: 'romance',
    count: 289,
    popular: true,
    description: 'Characters who are friends first develop romantic feelings for each other.'
  }, {
    id: '3',
    name: 'Fake Dating',
    category: 'romance',
    count: 245,
    popular: true,
    description: 'Characters pretend to be in a relationship but eventually develop real feelings.'
  }, {
    id: '4',
    name: 'Forced Proximity',
    category: 'romance',
    count: 234,
    popular: true,
    description: 'Characters are forced to spend time together in close quarters.'
  }, {
    id: '5',
    name: 'Second Chance Romance',
    category: 'romance',
    count: 212,
    popular: true,
    description: 'Former couples reunite and rekindle their relationship.'
  }, {
    id: '6',
    name: 'Slow Burn',
    category: 'romance',
    count: 198,
    popular: true,
    description: 'Romantic tension builds slowly throughout the story.'
  }, {
    id: '7',
    name: 'Forbidden Love',
    category: 'romance',
    count: 187,
    popular: true,
    description: 'Characters fall in love despite societal or other barriers.'
  }, {
    id: '8',
    name: 'Marriage of Convenience',
    category: 'romance',
    count: 156,
    popular: true,
    description: 'Characters marry for practical reasons but eventually fall in love.'
  }, {
    id: '9',
    name: 'Grumpy/Sunshine',
    category: 'romance',
    count: 134,
    popular: true,
    description: 'One character is grumpy while the other is perpetually cheerful.'
  }, {
    id: '10',
    name: 'Only One Bed',
    category: 'romance',
    count: 123,
    popular: true,
    description: 'Characters must share a bed due to circumstances.'
  },
  // Fantasy Tropes
  {
    id: '11',
    name: 'Chosen One',
    category: 'fantasy',
    count: 267,
    popular: true,
    description: 'A character is destined to save the world or fulfill a prophecy.'
  }, {
    id: '12',
    name: 'Found Family',
    category: 'fantasy',
    count: 245,
    popular: true,
    description: 'Characters form deep bonds that resemble family relationships.'
  }, {
    id: '13',
    name: 'Magical School',
    category: 'fantasy',
    count: 213,
    popular: true,
    description: 'The story takes place at a school for magic users.'
  }, {
    id: '14',
    name: 'Hidden World',
    category: 'fantasy',
    count: 178,
    popular: true,
    description: 'A magical world exists alongside or hidden from the normal world.'
  }, {
    id: '15',
    name: 'Quest',
    category: 'fantasy',
    count: 156,
    popular: true,
    description: 'Characters embark on a journey to accomplish a specific goal.'
  },
  // Mystery & Thriller Tropes
  {
    id: '16',
    name: 'Unreliable Narrator',
    category: 'mystery',
    count: 223,
    popular: true,
    description: 'The narrator of the story cannot be fully trusted.'
  }, {
    id: '17',
    name: 'Locked Room Mystery',
    category: 'mystery',
    count: 167,
    popular: true,
    description: 'A crime committed in a sealed environment with no apparent way for the perpetrator to enter or exit.'
  }, {
    id: '18',
    name: 'Red Herring',
    category: 'mystery',
    count: 156,
    popular: true,
    description: 'False clues that mislead the reader or characters.'
  }, {
    id: '19',
    name: 'Femme Fatale',
    category: 'mystery',
    count: 134,
    popular: true,
    description: 'A seductive woman who brings disaster to men who become involved with her.'
  },
  // Sci-Fi Tropes
  {
    id: '20',
    name: 'Time Travel',
    category: 'scifi',
    count: 189,
    popular: true,
    description: 'Characters can move between different points in time.'
  }, {
    id: '21',
    name: 'Dystopian Society',
    category: 'scifi',
    count: 178,
    popular: true,
    description: 'A society characterized by human misery, oppression, and disease.'
  }, {
    id: '22',
    name: 'First Contact',
    category: 'scifi',
    count: 156,
    popular: true,
    description: "Humanity's first encounter with extraterrestrial life."
  }, {
    id: '23',
    name: 'Artificial Intelligence',
    category: 'scifi',
    count: 145,
    popular: true,
    description: 'Stories involving sentient machines or computer systems.'
  },
  // Horror Tropes
  {
    id: '24',
    name: 'Haunted House',
    category: 'horror',
    count: 167,
    popular: true,
    description: 'A house or building inhabited by supernatural entities.'
  }, {
    id: '25',
    name: 'Final Girl',
    category: 'horror',
    count: 134,
    popular: true,
    description: 'The last woman alive to confront the killer in a slasher film.'
  }, {
    id: '26',
    name: 'Body Horror',
    category: 'horror',
    count: 123,
    popular: true,
    description: 'Horror involving the violation or distortion of the human body.'
  },
  // General Tropes
  {
    id: '27',
    name: 'Coming of Age',
    category: 'general',
    count: 245,
    popular: true,
    description: "A character's journey from youth to adulthood."
  }, {
    id: '28',
    name: 'Fish Out of Water',
    category: 'general',
    count: 189,
    popular: true,
    description: 'A character placed in an unfamiliar environment or situation.'
  }, {
    id: '29',
    name: 'Redemption Arc',
    category: 'general',
    count: 167,
    popular: true,
    description: "A character's journey from villain or anti-hero to hero."
  }, {
    id: '30',
    name: 'Underdog Story',
    category: 'general',
    count: 156,
    popular: true,
    description: 'A character or group faces overwhelming odds but ultimately succeeds.'
  }];
  // Filter and sort tropes based on user selections
  const filteredTropes = tropes.filter(trope => {
    // Filter by category
    if (selectedCategory !== 'all' && trope.category !== selectedCategory) {
      return false;
    }
    // Filter by search query
    if (searchQuery && !trope.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });
  // Sort the filtered tropes
  const sortedTropes = [...filteredTropes].sort((a, b) => {
    if (sortOption === 'alphabetical') {
      return a.name.localeCompare(b.name);
    } else if (sortOption === 'bookCount') {
      return b.count - a.count;
    } else if (sortOption === 'viewAll') {
      // For "View All", sort alphabetically regardless of category
      return a.name.localeCompare(b.name);
    } else {
      // Default: sort by popularity then by count
      if (a.popular !== b.popular) {
        return a.popular ? -1 : 1;
      }
      return b.count - a.count;
    }
  });
  // Group tropes by category for display (only if not in "View All" mode)
  const groupedTropes: Record<CategoryKey, Trope[]> = {
    romance: [],
    fantasy: [],
    mystery: [],
    scifi: [],
    horror: [],
    general: [],
    all: []
  };
  sortedTropes.forEach(trope => {
    groupedTropes[trope.category].push(trope);
  });
  // Get the category color for a trope
  const getTropeColor = (category: CategoryKey): string => {
    if (darkMode) {
      switch (category) {
        case 'romance':
          return 'bg-pink-900/30 text-pink-300';
        case 'fantasy':
          return 'bg-indigo-900/30 text-indigo-300';
        case 'mystery':
          return 'bg-amber-900/30 text-amber-300';
        case 'scifi':
          return 'bg-cyan-900/30 text-cyan-300';
        case 'horror':
          return 'bg-red-900/30 text-red-300';
        case 'general':
          return 'bg-gray-700 text-gray-300';
        default:
          return dk.badgeNeutral;
      }
    }
    const categoryObj = categories.find(c => c.key === category);
    return categoryObj?.color || 'bg-gray-100 text-gray-800';
  };
  const filterSections: FilterSection[] = [{
    id: 'category',
    title: 'Category',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Tropes'
    }, {
      value: 'romance',
      label: 'Romance'
    }, {
      value: 'fantasy',
      label: 'Fantasy'
    }, {
      value: 'mystery',
      label: 'Mystery & Thriller'
    }, {
      value: 'scifi',
      label: 'Sci-Fi'
    }, {
      value: 'horror',
      label: 'Horror'
    }, {
      value: 'general',
      label: 'General'
    }],
    value: selectedCategory,
    onChange: (val: string) => setSelectedCategory(val as CategoryKey)
  }, {
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'popularity',
      label: 'Popularity'
    }, {
      value: 'alphabetical',
      label: 'Alphabetical'
    }, {
      value: 'bookCount',
      label: 'Book Count'
    }, {
      value: 'viewAll',
      label: 'View All (flat list)'
    }],
    value: sortOption,
    onChange: (val: string) => setSortOption(val as typeof sortOption)
  }];
  const handleReset = () => {
    setSelectedCategory('all');
    setSortOption('popularity');
    setSearchQuery('');
  };
  const tropeColorClass = (t: {
    category?: string;
  }) => getTropeColor((t.category || 'general') as CategoryKey);
  const toDisplayTags = (items: Trope[]) => items.map(trope => ({
    id: trope.id,
    name: trope.name,
    description: trope.description,
    count: trope.count,
    category: trope.category,
    href: `/tagged-books/${trope.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`
  }));

  // Render the tropes
  const renderTropes = () => {
    if (sortOption === 'viewAll' || selectedCategory !== 'all' || searchQuery) {
      return <TagList tags={toDisplayTags(sortedTropes)} mode={tagViewMode} colorClassFor={tropeColorClass} data-id="element-15709" data-mp="1mtaweb33zd02" />;
    }
    const labelMap: Record<string, string> = {};
    categories.forEach(c => {
      labelMap[c.key] = c.label;
    });
    const sortedEntries = sortEntriesAlphabetically(Object.entries(groupedTropes).filter(([key]) => key !== 'all'), labelMap);
    return <div className="space-y-3" data-id="element-15710" data-mp="26i1rg0ltkvgg">
        {sortedEntries.map(([category, tropes]) => {
        const categoryObj = categories.find(c => c.key === category);
        return <ContentAccordionSection key={category} title={categoryObj?.label || category} icon={cloneElement(categoryObj?.icon || <HeartIcon className="h-5 w-5" data-id="element-15712" data-mp="l3nxwcm4dyep" />, {
          className: 'h-5 w-5'
        })} itemCount={tropes.length} data-id="element-15711" data-mp="1wib0ua0dr0ln">
              <TagList tags={toDisplayTags(tropes)} mode={tagViewMode} colorClassFor={tropeColorClass} data-id="element-15713" data-mp="fytye1jrmdnx" />
            </ContentAccordionSection>;
      })}
      </div>;
  };
  const contentNavItems: NavItem[] = [{
    label: 'View All Tags',
    href: '/tagged-books?type=all',
    icon: ListIcon
  }, {
    label: 'Tropes',
    href: '/tropes',
    icon: HeartHandshakeIcon,
    isActive: true,
    subItems: [{
      label: 'Romance',
      href: '/tropes?category=romance'
    }, {
      label: 'Fantasy',
      href: '/tropes?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/tropes?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/tropes?category=scifi'
    }, {
      label: 'Horror',
      href: '/tropes?category=horror'
    }, {
      label: 'General',
      href: '/tropes?category=general'
    }]
  }, {
    label: 'Characters',
    href: '/characters',
    icon: UserIcon,
    subItems: [{
      label: 'Romance',
      href: '/characters?category=romance'
    }, {
      label: 'Fantasy',
      href: '/characters?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/characters?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/characters?category=scifi'
    }, {
      label: 'Horror',
      href: '/characters?category=horror'
    }, {
      label: 'General',
      href: '/characters?category=general'
    }]
  }, {
    label: 'Relationship Types',
    href: '/relationship-types',
    icon: UsersIcon,
    subItems: [{
      label: 'Romance',
      href: '/relationship-types?category=romance'
    }, {
      label: 'Fantasy',
      href: '/relationship-types?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/relationship-types?category=mystery'
    }, {
      label: 'General',
      href: '/relationship-types?category=general'
    }]
  }, {
    label: 'Pairing Types',
    href: '/pairing-types',
    icon: LinkIcon,
    subItems: [{
      label: 'Monogamous',
      href: '/pairing-types?category=monogamous'
    }, {
      label: 'Why Choose',
      href: '/pairing-types?category=why-choose'
    }, {
      label: 'Closed Triad',
      href: '/pairing-types?category=closed-triad'
    }, {
      label: 'Polyamorous',
      href: '/pairing-types?category=polyamorous'
    }, {
      label: 'NB / Trans Inclusive',
      href: '/pairing-types?category=nb-trans'
    }, {
      label: 'No Romance',
      href: '/pairing-types?category=no-romance'
    }]
  }, {
    label: 'Spice & Darkness',
    href: '/spice-darkness',
    icon: FlameIcon,
    subItems: [{
      label: 'Spice Levels',
      href: '/spice-darkness#spice'
    }, {
      label: 'Darkness Levels',
      href: '/spice-darkness#darkness'
    }]
  }, {
    label: 'Burn Levels',
    href: '/burn-levels',
    icon: ZapIcon
  }, {
    label: 'Ending Types',
    href: '/ending-types',
    icon: BookmarkIcon
  }, {
    label: 'Representation',
    href: '/representation',
    icon: UsersIcon,
    subItems: [{
      label: 'Sexual Orientation & Gender',
      href: '/representation?category=Sexual+Orientation+%26+Gender'
    }, {
      label: 'Race & Ethnicity',
      href: '/representation?category=Race+%26+Ethnicity'
    }, {
      label: 'Neurodiversity & Disability',
      href: '/representation?category=Neurodiversity+%26+Disability'
    }, {
      label: 'Mental Health',
      href: '/representation?category=Mental+Health'
    }]
  }, {
    label: 'Cultural Identity',
    href: '/cultural-identity',
    icon: GlobeIcon
  }, {
    label: 'Themes',
    href: '/themes',
    icon: BookOpenIcon,
    subItems: [{
      label: 'Personal Growth',
      href: '/themes?category=Personal+Growth'
    }, {
      label: 'Destiny',
      href: '/themes?category=Destiny'
    }, {
      label: 'Power',
      href: '/themes?category=Power'
    }, {
      label: 'Morality',
      href: '/themes?category=Morality'
    }, {
      label: 'Relationships',
      href: '/themes?category=Relationships'
    }, {
      label: 'Identity',
      href: '/themes?category=Identity'
    }]
  }, {
    label: 'Settings',
    href: '/settings',
    icon: MapPinIcon,
    subItems: [{
      label: 'Contemporary',
      href: '/settings?category=Contemporary'
    }, {
      label: 'Historical',
      href: '/settings?category=Historical'
    }, {
      label: 'Fantasy',
      href: '/settings?category=Fantasy'
    }, {
      label: 'Science Fiction',
      href: '/settings?category=Science%20Fiction'
    }]
  }, {
    label: 'Content Warnings',
    href: '/content-warnings',
    icon: AlertCircleIcon
  }];
  return <div className={dk.page} data-id="element-15714" data-mp="2e2myi5ajtgrt">
      <ContentPageHeader title="Tropes" icon={getContentPageIcon('tropes')} searchPlaceholder="Search tropes..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={sidebarOpen} setShowFilters={setSidebarOpen} data-id="element-15715" data-mp="2e1kbw9k9e0hp" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-15716" data-mp="26jvupzalh8nc">
        <div className="flex gap-8" data-id="element-15717" data-mp="21iye3r58gmny">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-15718" data-mp="21hwtauyf0lt1">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={filterSections} onReset={handleReset} searchable={false} data-id="element-15719" data-mp="1nscvm3joyeql" />
          </FilterSidebarWrapper>
          <main className="flex-1 min-w-0" data-id="element-15720" data-mp="1dvhynp32gmef">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-id="element-15721" data-mp="19s23yyak4w4d">
              <p className={`text-sm ${dk.textSecondary}`} data-id="element-15722" data-mp="14t8nqy3vyr0i">
                {sortedTropes.length}{' '}
                {sortedTropes.length === 1 ? 'trope' : 'tropes'}
              </p>
              <TagViewToggle mode={tagViewMode} setMode={setTagViewMode} data-id="element-15723" data-mp="v7pln7e8op9z" />
            </div>
            {sortedTropes.length === 0 ? <div className="text-center py-12" data-id="element-15724" data-mp="l7ynmvqgzs55">
                <p className={dk.textMuted} data-id="element-15725" data-mp="l99r0ug06xvq">
                  No tropes found matching your criteria.
                </p>
              </div> : renderTropes()}
          </main>
        </div>
      </div>
    </div>;
};
export default Tropes;